import { Controller } from "@hotwired/stimulus";
import {
  initAddTagsModal,
  showAddTagsModal,
  applyContentTags,
  executeAddToLibrary,
  removeFromLibrary,
  setAddButtonState,
  setRemoveButtonState,
} from "../shared/library_tags";
import {
  observeSentinel,
  disconnectObserver,
  scrollToTop,
} from "../shared/infinite_scroll";

export default class extends Controller {
  static targets = [
    "query",
    "results",
    "form",
    "grid",
    "sentinel",
    "spinner",
    "releasedTab",
    "comingSoonTab",
    "tabInput",
  ];
  static values = { url: String, hasFriends: Boolean };

  connect() {
    this.debounceTimer = null;
    this.loading = false;
    observeSentinel(this);
    this.restoreFilters();
    this.addTagsModal = initAddTagsModal();
  }

  disconnect() {
    clearTimeout(this.debounceTimer);
    disconnectObserver(this);
  }

  debounceSearch() {
    clearTimeout(this.debounceTimer);
    const value = this.queryTarget.value.trim();

    if (value.length === 0 || value.length >= 2) {
      this.debounceTimer = setTimeout(() => {
        this.saveFilters();
        this.fetchResults();
      }, 500);
    }
  }

  filter() {
    this.saveFilters();
    this.fetchResults();
  }

  showReleased() {
    this.tabInputTarget.value = "released";
    this.releasedTabTarget.classList.add("active");
    this.comingSoonTabTarget.classList.remove("active");
    this.saveFilters();
    this.fetchResults();
  }

  showComingSoon() {
    this.tabInputTarget.value = "coming_soon";
    this.comingSoonTabTarget.classList.add("active");
    this.releasedTabTarget.classList.remove("active");
    this.saveFilters();
    this.fetchResults();
  }

  saveFilters() {
    const formData = new FormData(this.formTarget);
    const data = {};
    for (const [key, value] of formData.entries()) {
      data[key] = value;
    }
    localStorage.setItem("bingeflow_new_content_filters", JSON.stringify(data));
  }

  restoreFilters() {
    const saved = localStorage.getItem("bingeflow_new_content_filters");
    if (!saved) return;
    const data = JSON.parse(saved);
    const form = this.formTarget;
    const query = form.querySelector('[name="q"]');
    if (query && data.q) query.value = data.q;
    const provider = form.querySelector('[name="provider"]');
    if (provider && data.provider) provider.value = data.provider;
    const category = form.querySelector('[name="category"]');
    if (category && data.category) category.value = data.category;
    if (data.tab === "coming_soon") {
      this.tabInputTarget.value = "coming_soon";
      this.comingSoonTabTarget.classList.add("active");
      this.releasedTabTarget.classList.remove("active");
    }
    this.fetchResults();
  }

  fetchResults() {
    const formData = new FormData(this.formTarget);
    const params = new URLSearchParams(formData).toString();
    const url = this.urlValue + "?" + params;

    fetch(url, {
      headers: { "X-Requested-With": "XMLHttpRequest" },
    })
      .then((response) => response.text())
      .then((html) => {
        this.resultsTarget.innerHTML = html;
        observeSentinel(this);
      });
  }

  loadMore() {
    if (this.loading || !this.hasSentinelTarget) return;

    const nextPage = this.sentinelTarget.dataset.nextPage;
    if (!nextPage) return;

    this.loading = true;
    disconnectObserver(this);
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.remove("d-none");

    const formData = new FormData(this.formTarget);
    formData.set("page", nextPage);
    const params = new URLSearchParams(formData).toString();
    const url = this.urlValue + "?" + params;

    fetch(url, {
      headers: { "X-Requested-With": "XMLHttpRequest" },
    })
      .then((response) => response.text())
      .then((html) => {
        if (this.hasSpinnerTarget) this.spinnerTarget.remove();
        if (this.hasSentinelTarget) this.sentinelTarget.remove();

        const temp = document.createElement("div");
        temp.innerHTML = html;

        const cards = temp.querySelectorAll(".new-content-card-wrapper");
        cards.forEach((card) => this.gridTarget.appendChild(card));

        const match = html.match(/<!-- next_page:(\d+) -->/);
        if (match) {
          const sentinel = document.createElement("div");
          sentinel.dataset.newContentTarget = "sentinel";
          sentinel.dataset.nextPage = match[1];
          this.resultsTarget.appendChild(sentinel);

          const spinner = document.createElement("div");
          spinner.className = "search-loading-spinner";
          spinner.dataset.newContentTarget = "spinner";
          spinner.innerHTML =
            '<div class="spinner-border" role="status"><span class="visually-hidden">Loading...</span></div>';
          this.resultsTarget.insertBefore(spinner, sentinel);

          observeSentinel(this);
        }

        this.loading = false;
      });
  }

  addToLibrary(event) {
    event.preventDefault();
    event.stopPropagation();

    const btn = event.currentTarget;
    const slug = btn.dataset.contentSlug;
    const name = btn.dataset.contentName;

    if (this.hasFriendsValue) {
      showAddTagsModal(this.addTagsModal, slug, name, {
        onApply: (checkboxes) => {
          this.doAddToLibrary(btn, slug, true).then(() =>
            applyContentTags(slug, checkboxes).then(() =>
              this.addTagsModal.hide(),
            ),
          );
        },
        onSkip: () => {
          this.doAddToLibrary(btn, slug).then(() =>
            this.addTagsModal.hide(),
          );
        },
      });
      return;
    }

    this.doAddToLibrary(btn, slug);
  }

  doAddToLibrary(btn, slug, skipEvent = false) {
    btn.disabled = true;

    return executeAddToLibrary(slug, { skipEvent })
      .then(() => {
        setAddButtonState(btn, "new-content");
      })
      .catch(() => {
        btn.disabled = false;
      });
  }

  removeFromLibrary(event) {
    event.preventDefault();
    event.stopPropagation();

    const btn = event.currentTarget;
    this.pendingRemoveSlug = btn.dataset.contentSlug;
    this.pendingRemoveBtn = btn;

    const name = btn.dataset.contentName;
    document.getElementById("RemoveContentName").textContent = name;
    document.getElementById("RemoveContentNameLabel").textContent = name;

    const checkbox = document.getElementById("RemoveCheckbox");
    const confirmBtn = document.getElementById("RemoveConfirmBtn");
    checkbox.checked = false;
    confirmBtn.disabled = true;

    const modal = new bootstrap.Modal(document.getElementById("RemoveModal"));
    this.removeModal = modal;

    checkbox.onchange = () => {
      confirmBtn.disabled = !checkbox.checked;
    };
    confirmBtn.onclick = () => this.confirmRemove();

    modal.show();
  }

  confirmRemove() {
    const slug = this.pendingRemoveSlug;
    const btn = this.pendingRemoveBtn;
    const confirmBtn = document.getElementById("RemoveConfirmBtn");
    confirmBtn.disabled = true;

    removeFromLibrary(slug)
      .then(() => {
        this.removeModal.hide();
        setRemoveButtonState(btn, "new-content");
      })
      .catch(() => {
        confirmBtn.disabled = false;
      });
  }

  scrollToTop(event) {
    scrollToTop(event);
  }
}
